import {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/auth";
import {
  apiClient,
  clearTokenGetter,
  setTokenGetter,
  setAuthErrorHandler,
  clearAuthErrorHandler,
  isTokenGetterSet,
} from "@/services/api";
import { toast } from "sonner";

/**
 * Context to track whether the API client is ready for authenticated requests
 */
interface ApiReadyContextType {
  isApiReady: boolean;
}

const ApiReadyContext = createContext<ApiReadyContextType>({ isApiReady: false });

/**
 * Hook to check if the API client is ready for authenticated requests.
 * Use this in the `enabled` option of React Query hooks to prevent
 * premature API calls.
 */
export function useApiReady(): boolean {
  const context = useContext(ApiReadyContext);
  return context.isApiReady;
}

/**
 * Provider Props
 */
interface ApiProviderProps {
  children: ReactNode;
}

/**
 * ApiProvider Component
 *
 * Wraps components that need authenticated API access.
 * Initializes the API client with Auth0 token getter and provides
 * a context to track when the API is ready.
 *
 * Usage:
 * ```typescript
 * // In ProtectedRoute or App.tsx
 * <ApiProvider>
 *   <YourProtectedContent />
 * </ApiProvider>
 * ```
 */
export function ApiProvider({ children }: ApiProviderProps) {
  const { getAccessToken, isAuthenticated, isLoading, logout } = useAuth();
  const [isApiReady, setIsApiReady] = useState(false);
  const hasInitializedRef = useRef(false);

  const handleAuthError = useCallback((message?: string) => {
    if (message) {
      toast.error(message);
    }
    console.log("Auth error detected, logging out user...");
    logout({ logoutParams: { returnTo: window.location.origin + "/login" } });
  }, [logout]);

  const tokenGetter = useMemo(() => {
    return async (options?: { forceRefresh?: boolean }) => {
      try {
        const token = await getAccessToken(options);
        if (!token) {
          throw new Error("getAccessToken returned empty token");
        }
        return token;
      } catch (error) {
        console.error("Error getting access token:", error);
        throw error;
      }
    };
  }, [getAccessToken]);

  // CRITICAL: Initialize token getter SYNCHRONOUSLY during render
  // This ensures it's available before any child hooks run
  if (isAuthenticated && !isLoading && !hasInitializedRef.current) {
    setTokenGetter(tokenGetter);
    setAuthErrorHandler(handleAuthError);
    hasInitializedRef.current = true;
    // Note: Can't call setIsApiReady here (during render), but the sync setup is what matters
  }

  // Use effect to update the ready state and handle cleanup
  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      // User logged out or auth is still loading
      if (hasInitializedRef.current) {
        clearTokenGetter();
        clearAuthErrorHandler();
        hasInitializedRef.current = false;
      }
      setIsApiReady(false);
      return;
    }

    // Ensure token getter is set (handles edge cases like hot reload)
    if (!isTokenGetterSet()) {
      setTokenGetter(tokenGetter);
      setAuthErrorHandler(handleAuthError);
      hasInitializedRef.current = true;
    }

    // Mark API as ready
    setIsApiReady(true);

    // Cleanup on unmount
    return () => {
      clearTokenGetter();
      clearAuthErrorHandler();
      hasInitializedRef.current = false;
      setIsApiReady(false);
    };
  }, [isAuthenticated, isLoading, tokenGetter, handleAuthError]);

  return (
    <ApiReadyContext.Provider value={{ isApiReady }}>
      {children}
    </ApiReadyContext.Provider>
  );
}

/**
 * useApi Hook
 *
 * Returns the API client. Should be used within an ApiProvider.
 * For checking if API is ready, use useApiReady() hook.
 */
export function useApi() {
  return apiClient;
}
